import { useState } from 'react';

interface Section {
  id: string;
  icon: string;
  title: string;
  badge?: string;
  content: React.ReactNode;
}

function SectionBlock({ section, isActive, onClick }: { section: Section; isActive: boolean; onClick: () => void }) {
  return (
    <div className="border border-beige-dark rounded-lg overflow-hidden mb-3 transition-all duration-200">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-beige/50 transition-colors cursor-pointer text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal/10">
            <i className={`${section.icon} text-teal text-base`} />
          </div>
          <span className="font-sans font-700 text-navy text-sm">{section.title}</span>
          {section.badge && (
            <span className="bg-coral/10 text-coral text-xs font-600 font-sans px-2 py-0.5 rounded-full">{section.badge}</span>
          )}
        </div>
        <i className={`ri-arrow-${isActive ? 'up' : 'down'}-s-line text-navy/40 text-lg`} />
      </button>
      {isActive && (
        <div className="px-6 py-5 bg-white border-t border-beige-dark">
          {section.content}
        </div>
      )}
    </div>
  );
}

function DataItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-beige last:border-0">
      <div className="w-7 h-7 flex items-center justify-center shrink-0">
        <i className={`${icon} text-teal text-base`} />
      </div>
      <div>
        <p className="text-xs font-700 font-sans text-navy/50 uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-sm font-sans text-navy/80">{value}</p>
      </div>
    </div>
  );
}

function RightsCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-beige/60 rounded-lg p-4 border border-beige-dark">
      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-coral/10 mb-3">
        <i className={`${icon} text-coral text-base`} />
      </div>
      <h4 className="font-sans font-700 text-navy text-sm mb-1">{title}</h4>
      <p className="font-sans text-navy/65 text-xs leading-relaxed">{desc}</p>
    </div>
  );
}

const paragraphStyle = 'font-sans text-sm text-navy/70 leading-relaxed mb-3 last:mb-0';
const listStyle = 'space-y-1.5 mt-2';
const listItemStyle = 'flex items-start gap-2 font-sans text-sm text-navy/70';
const bulletStyle = 'w-1.5 h-1.5 rounded-full bg-coral mt-1.5 shrink-0';

export default function LGPDSections() {
  const [activeSection, setActiveSection] = useState<string>('1');

  const toggle = (id: string) => setActiveSection(prev => prev === id ? '' : id);

  const sections: Section[] = [
    {
      id: '1',
      icon: 'ri-user-settings-line',
      title: '1. Quem somos — Controlador dos Dados',
      content: (
        <div>
          <p className={paragraphStyle}>
            A <strong>Neuro Elos</strong> é a controladora responsável pelo tratamento dos dados pessoais
            coletados neste site e durante o processo de avaliação neuropsicopedagógica infantil.
          </p>
          <div className="bg-beige/60 rounded-lg p-4 mt-3 space-y-0">
            <DataItem icon="ri-building-line" label="Responsável" value="Neuro Elos — Avaliação Neuropsicopedagógica Infantil" />
            <DataItem icon="ri-mail-line" label="E-mail de contato" value="contato@neuroelos.com.br" />
            <DataItem icon="ri-whatsapp-line" label="WhatsApp" value="(11) 98188-2825" />
            <DataItem icon="ri-map-pin-line" label="Atendimento" value="Presencial e online" />
          </div>
        </div>
      ),
    },
    {
      id: '2',
      icon: 'ri-database-2-line',
      title: '2. Dados Coletados',
      badge: 'Dados Sensíveis',
      content: (
        <div>
          <p className={paragraphStyle}>
            Coletamos diferentes categorias de dados conforme a finalidade, incluindo dados pessoais
            comuns e <strong>dados sensíveis</strong> (saúde, dados de crianças), nos termos dos artigos 5°, 11 e 14 da LGPD.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-teal/5 border border-teal/20 rounded-lg p-4">
              <p className="font-sans font-700 text-teal text-xs uppercase tracking-wide mb-3">
                <i className="ri-user-line mr-1" />Dados do Responsável
              </p>
              <ul className={listStyle}>
                {['Nome completo', 'E-mail', 'Telefone / WhatsApp', 'Relação com a criança'].map(i => (
                  <li key={i} className={listItemStyle}><span className={bulletStyle} />{i}</li>
                ))}
              </ul>
            </div>

            <div className="bg-coral/5 border border-coral/20 rounded-lg p-4">
              <p className="font-sans font-700 text-coral text-xs uppercase tracking-wide mb-3">
                <i className="ri-child-line mr-1" />Dados da Criança (Art. 14 LGPD)
              </p>
              <ul className={listStyle}>
                {['Nome, data de nascimento, idade', 'Histórico de desenvolvimento', 'Queixas e comportamentos relatados', 'Histórico escolar e médico'].map(i => (
                  <li key={i} className={listItemStyle}><span className={bulletStyle} />{i}</li>
                ))}
              </ul>
            </div>

            <div className="bg-navy/5 border border-navy/20 rounded-lg p-4">
              <p className="font-sans font-700 text-navy text-xs uppercase tracking-wide mb-3">
                <i className="ri-heart-pulse-line mr-1" />Dados de Saúde (Sensíveis — Art. 11)
              </p>
              <ul className={listStyle}>
                {['Laudos e diagnósticos anteriores', 'Medicamentos em uso', 'Histórico neurológico e psicológico', 'Resultados de avaliações aplicadas'].map(i => (
                  <li key={i} className={listItemStyle}><span className={bulletStyle} />{i}</li>
                ))}
              </ul>
            </div>

            <div className="bg-beige border border-beige-dark rounded-lg p-4">
              <p className="font-sans font-700 text-navy/50 text-xs uppercase tracking-wide mb-3">
                <i className="ri-global-line mr-1" />Dados de Navegação (Site)
              </p>
              <ul className={listStyle}>
                {['Endereço IP (anonimizado)', 'Páginas visitadas', 'Tempo de permanência', 'Dispositivo e navegador'].map(i => (
                  <li key={i} className={listItemStyle}><span className={bulletStyle} />{i}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: '3',
      icon: 'ri-focus-3-line',
      title: '3. Finalidades do Tratamento',
      content: (
        <div>
          <p className={paragraphStyle}>
            Seus dados são tratados exclusivamente para as finalidades abaixo, com base legal adequada para cada uma (Art. 7° e 11 da LGPD):
          </p>
          <div className="space-y-3 mt-3">
            {[
              { base: 'Consentimento', icon: 'ri-checkbox-circle-line', color: 'text-teal', title: 'Envio de newsletter e conteúdo científico', desc: 'Somente com seu consentimento expresso, mediante aceite no formulário de inscrição.' },
              { base: 'Execução de contrato', icon: 'ri-file-paper-line', color: 'text-coral', title: 'Realização da avaliação neuropsicopedagógica', desc: 'Agendamento, aplicação dos protocolos, elaboração de relatório e devolutiva.' },
              { base: 'Legítimo interesse / Saúde', icon: 'ri-heart-pulse-line', color: 'text-navy', title: 'Proteção integral da criança avaliada', desc: 'Dados de saúde tratados para fins de diagnóstico diferencial e orientação especializada.' },
              { base: 'Obrigação legal', icon: 'ri-scales-3-line', color: 'text-teal-dark', title: 'Cumprimento de obrigações legais e profissionais', desc: 'Retenção de prontuários conforme o CFP (Conselho Federal de Psicologia) e normativas do MEC/CBO.' },
              { base: 'Legítimo interesse', icon: 'ri-bar-chart-line', color: 'text-coral-dark', title: 'Melhoria contínua dos serviços', desc: 'Análise de dados anonimizados para aprimorar metodologias e instrumentos de avaliação.' },
            ].map(item => (
              <div key={item.title} className="flex gap-3 bg-beige/50 rounded-lg p-4 border border-beige-dark">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} ${item.color} text-lg`} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-sans font-700 text-navy text-sm">{item.title}</p>
                    <span className="text-xs font-sans text-white bg-navy/40 px-2 py-0.5 rounded-full hidden sm:inline">{item.base}</span>
                  </div>
                  <p className="font-sans text-xs text-navy/60 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: '4',
      icon: 'ri-child-line',
      title: '4. Proteção Especial de Dados de Crianças',
      badge: 'Art. 14 LGPD',
      content: (
        <div>
          <div className="bg-coral/8 border border-coral/25 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-coral text-lg" />
              </div>
              <p className="font-sans text-sm text-navy/75 leading-relaxed">
                O Art. 14 da LGPD confere proteção especial ao tratamento de dados pessoais de crianças
                (menores de 12 anos) e adolescentes. Todos os serviços desta plataforma destinam-se ao
                público infantil, portanto estas regras se aplicam integralmente.
              </p>
            </div>
          </div>

          <ul className={listStyle}>
            {[
              'O consentimento para coleta e tratamento dos dados da criança é sempre obtido junto ao pai, mãe ou responsável legal.',
              'Nunca coletamos dados de crianças diretamente — toda comunicação é realizada com o responsável.',
              'Os dados da criança não são utilizados para fins comerciais, publicitários ou de perfilamento.',
              'O acesso ao prontuário e laudos é restrito ao responsável legal e aos profissionais envolvidos no processo.',
              'Na devolução, os dados são apresentados exclusivamente aos responsáveis legais, preservando a dignidade da criança.',
              'O responsável pode revogar o consentimento a qualquer momento, salvo quando o tratamento for necessário para proteção da criança.',
            ].map(item => (
              <li key={item} className={listItemStyle}>
                <span className={bulletStyle} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: '5',
      icon: 'ri-time-line',
      title: '5. Retenção e Armazenamento de Dados',
      content: (
        <div>
          <p className={paragraphStyle}>Os dados são retidos pelo prazo mínimo necessário a cada finalidade:</p>
          <div className="overflow-x-auto mt-3 rounded-lg border border-beige-dark">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="bg-teal/10">
                  <th className="text-left px-4 py-3 text-teal font-700 text-xs uppercase tracking-wide">Tipo de Dado</th>
                  <th className="text-left px-4 py-3 text-teal font-700 text-xs uppercase tracking-wide">Prazo de Retenção</th>
                  <th className="text-left px-4 py-3 text-teal font-700 text-xs uppercase tracking-wide">Base Legal</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Prontuário / Relatório de Avaliação', '5 anos (CFP — Resolução 001/2009)', 'Obrigação Legal'],
                  ['Dados de saúde da criança', '5 anos após o último atendimento', 'Obrigação Legal / Saúde'],
                  ['Formulário de Pré-Anamnese', 'Duração do processo + 2 anos', 'Execução de Contrato'],
                  ['Dados de newsletter', 'Até a revogação do consentimento', 'Consentimento'],
                  ['Dados de navegação', '6 meses (anonimizados)', 'Legítimo Interesse'],
                ].map(([tipo, prazo, base]) => (
                  <tr key={tipo} className="border-t border-beige-dark hover:bg-beige/40 transition-colors">
                    <td className="px-4 py-3 text-navy/80 font-600">{tipo}</td>
                    <td className="px-4 py-3 text-navy/65">{prazo}</td>
                    <td className="px-4 py-3">
                      <span className="bg-teal/10 text-teal text-xs font-600 px-2 py-0.5 rounded-full">{base}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={`${paragraphStyle} mt-3`}>
            Após o vencimento do prazo, os dados são eliminados de forma segura ou anonimizados,
            salvo obrigação legal de retenção.
          </p>
        </div>
      ),
    },
    {
      id: '6',
      icon: 'ri-share-line',
      title: '6. Compartilhamento com Terceiros',
      content: (
        <div>
          <p className={paragraphStyle}>
            <strong>Não vendemos ou comercializamos dados.</strong> O compartilhamento ocorre apenas
            nos casos abaixo, sempre com salvaguardas adequadas:
          </p>
          <div className="space-y-3 mt-3">
            {[
              { icon: 'ri-hospital-line', who: 'Outros profissionais de saúde', when: 'Mediante solicitação expressa do responsável legal para fins de encaminhamento terapêutico.' },
              { icon: 'ri-government-line', who: 'Autoridades públicas e judiciais', when: 'Exclusivamente quando exigido por ordem judicial ou obrigação legal prevista em lei.' },
              { icon: 'ri-cloud-line', who: 'Fornecedores de tecnologia', when: 'Plataformas de e-mail e armazenamento seguro em nuvem — com cláusulas contratuais de proteção de dados.' },
              { icon: 'ri-user-star-line', who: 'Supervisores clínicos', when: 'Em contextos de supervisão profissional, com dados anonimizados ou com consentimento expresso.' },
            ].map(item => (
              <div key={item.who} className="flex gap-3 bg-beige/50 rounded-lg p-4 border border-beige-dark">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-teal text-base`} />
                </div>
                <div>
                  <p className="font-sans font-700 text-navy text-sm mb-0.5">{item.who}</p>
                  <p className="font-sans text-xs text-navy/60 leading-relaxed">{item.when}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: '7',
      icon: 'ri-shield-keyhole-line',
      title: '7. Segurança dos Dados',
      content: (
        <div>
          <p className={paragraphStyle}>
            Adotamos medidas técnicas e organizacionais alinhadas às melhores práticas de segurança
            da informação para proteger os dados contra acesso não autorizado, perda ou vazamento:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            {[
              { icon: 'ri-lock-password-line', text: 'Criptografia TLS em todas as transmissões' },
              { icon: 'ri-key-2-line', text: 'Acesso restrito por credenciais seguras' },
              { icon: 'ri-folder-lock-line', text: 'Armazenamento criptografado de prontuários' },
              { icon: 'ri-eye-off-line', text: 'Política de sigilo profissional (CFP)' },
              { icon: 'ri-smartphone-line', text: 'Autenticação de dois fatores nos sistemas' },
              { icon: 'ri-alert-line', text: 'Plano de resposta a incidentes de segurança' },
            ].map(item => (
              <div key={item.text} className="flex flex-col items-center text-center bg-beige/60 rounded-lg p-4 border border-beige-dark">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal/10 mb-2">
                  <i className={`${item.icon} text-teal text-lg`} />
                </div>
                <p className="font-sans text-xs text-navy/70 leading-snug">{item.text}</p>
              </div>
            ))}
          </div>
          <p className={`${paragraphStyle} mt-4`}>
            Em caso de incidente de segurança, notificaremos a ANPD e os titulares afetados no prazo
            de 72 horas, conforme Art. 48 da LGPD.
          </p>
        </div>
      ),
    },
    {
      id: '8',
      icon: 'ri-user-heart-line',
      title: '8. Seus Direitos como Titular',
      content: (
        <div>
          <p className={paragraphStyle}>
            A LGPD (Art. 18) garante ao titular — e ao responsável legal da criança — os seguintes direitos:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {[
              { icon: 'ri-search-eye-line', title: 'Acesso', desc: 'Solicitar confirmação e acesso a todos os dados que temos sobre você e seu filho.' },
              { icon: 'ri-edit-line', title: 'Correção', desc: 'Solicitar a atualização ou correção de dados incompletos, inexatos ou desatualizados.' },
              { icon: 'ri-delete-bin-line', title: 'Eliminação', desc: 'Solicitar a exclusão dos dados tratados com base no consentimento.' },
              { icon: 'ri-file-download-line', title: 'Portabilidade', desc: 'Receber seus dados em formato estruturado para transferência a outro serviço.' },
              { icon: 'ri-close-circle-line', title: 'Revogação', desc: 'Retirar o consentimento a qualquer momento, sem prejuízo ao tratamento anterior.' },
              { icon: 'ri-information-line', title: 'Informação', desc: 'Saber com quais terceiros seus dados são compartilhados e as bases legais aplicadas.' },
            ].map(r => <RightsCard key={r.title} {...r} />)}
          </div>
          <div className="mt-4 bg-teal/8 border border-teal/20 rounded-lg p-4">
            <p className="font-sans text-sm text-navy/70">
              <strong>Para exercer seus direitos,</strong> entre em contato pelo e-mail{' '}
              <a href="mailto:contato@neuroelos.com.br" className="text-teal font-600 cursor-pointer hover:underline">
                contato@neuroelos.com.br
              </a>{' '}
              ou WhatsApp{' '}
              <a href="https://wa.me/5511981882825" target="_blank" rel="noopener noreferrer" className="text-teal font-600 cursor-pointer hover:underline">
                (11) 98188-2825
              </a>.
              Responderemos em até <strong>15 dias úteis</strong>, conforme Art. 18 §5° da LGPD.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: '9',
      icon: 'ri-cookie-line',
      title: '9. Cookies e Tecnologias de Rastreamento',
      content: (
        <div>
          <p className={paragraphStyle}>
            Utilizamos apenas cookies essenciais ao funcionamento do site. Não utilizamos cookies de
            rastreamento publicitário ou ferramentas de perfilamento comportamental.
          </p>
          <div className="overflow-x-auto mt-3 rounded-lg border border-beige-dark">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="bg-teal/10">
                  <th className="text-left px-4 py-3 text-teal font-700 text-xs uppercase tracking-wide">Cookie</th>
                  <th className="text-left px-4 py-3 text-teal font-700 text-xs uppercase tracking-wide">Finalidade</th>
                  <th className="text-left px-4 py-3 text-teal font-700 text-xs uppercase tracking-wide">Duração</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Sessão', 'Manter o estado da navegação no formulário de Pré-Anamnese', 'Sessão'],
                  ['Preferências', 'Memorizar idioma e configurações de acessibilidade', '30 dias'],
                  ['Analytics (anon.)', 'Contar visitas anonimamente para melhorar o site', '6 meses'],
                ].map(([cookie, finalidade, duracao]) => (
                  <tr key={cookie} className="border-t border-beige-dark hover:bg-beige/40 transition-colors">
                    <td className="px-4 py-3 text-navy/80 font-600">{cookie}</td>
                    <td className="px-4 py-3 text-navy/65">{finalidade}</td>
                    <td className="px-4 py-3 text-navy/65">{duracao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      id: '10',
      icon: 'ri-notification-3-line',
      title: '10. Alterações nesta Política',
      content: (
        <div>
          <p className={paragraphStyle}>
            Esta Política de Privacidade pode ser atualizada periodicamente para refletir mudanças
            na legislação, nas nossas práticas ou nos serviços oferecidos.
          </p>
          <ul className={listStyle}>
            {[
              'Alterações relevantes serão comunicadas por e-mail aos titulares cadastrados.',
              'A data de "última atualização" no topo desta página será sempre revisada.',
              'O uso continuado dos serviços após a publicação de alterações implica aceite das novas condições.',
              'Para mudanças que afetem o tratamento de dados de crianças, o consentimento será solicitado novamente.',
            ].map(item => (
              <li key={item} className={listItemStyle}><span className={bulletStyle} /><span>{item}</span></li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: '11',
      icon: 'ri-customer-service-2-line',
      title: '11. Contato e Encarregado de Dados (DPO)',
      content: (
        <div>
          <p className={paragraphStyle}>
            Para dúvidas, solicitações ou reclamações sobre o tratamento de seus dados pessoais,
            entre em contato com nosso Encarregado de Proteção de Dados:
          </p>
          <div className="bg-beige/60 rounded-lg p-4 mt-3 space-y-0">
            <DataItem icon="ri-user-star-line" label="Encarregado (DPO)" value="Equipe Neuro Elos" />
            <DataItem icon="ri-mail-send-line" label="E-mail" value="contato@neuroelos.com.br" />
            <DataItem icon="ri-whatsapp-line" label="WhatsApp" value="(11) 98188-2825" />
            <DataItem icon="ri-time-line" label="Prazo de resposta" value="Até 15 dias úteis" />
          </div>
          <div className="mt-4 bg-navy/5 border border-navy/10 rounded-lg p-4">
            <p className="font-sans text-xs text-navy/60 leading-relaxed">
              Se não ficar satisfeito com nossa resposta, você pode registrar reclamação junto à{' '}
              <strong>ANPD — Autoridade Nacional de Proteção de Dados</strong>{' '}
              (<a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer nofollow" className="text-teal cursor-pointer hover:underline">www.gov.br/anpd</a>).
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="bg-cream py-16">
      <div className="max-w-4xl mx-auto px-6">

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: 'ri-shield-check-line', label: 'Dados Protegidos', color: 'text-teal' },
            { icon: 'ri-child-line', label: 'Proteção Infantil', color: 'text-coral' },
            { icon: 'ri-lock-line', label: 'Criptografia Total', color: 'text-navy' },
            { icon: 'ri-scales-3-line', label: 'LGPD Compliant', color: 'text-teal-dark' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-lg p-5 text-center border border-beige-dark">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className={`${item.icon} ${item.color} text-2xl`} />
              </div>
              <p className="font-sans font-700 text-navy text-xs">{item.label}</p>
            </div>
          ))}
        </div>

        <div>
          {sections.map(section => (
            <SectionBlock
              key={section.id}
              section={section}
              isActive={activeSection === section.id}
              onClick={() => toggle(section.id)}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
